// Los cinco handlers del login, sin React: lo que antes vivía inline en
// login/page.tsx. Separados para poder probar en jest-node (sin DOM) la única
// garantía que importa aquí: el spinner SIEMPRE se apaga.
//
// S5-4 (P1-17 · Sentry REDY-X): `handleVerifyCode` y `handlePasswordSubmit` no
// tenían `try`. Si la server action lanzaba —respuesta no-RSC de la plataforma,
// red caída, desfase de versión— el botón se quedaba en «Verificando...» para
// siempre y el rechazo salía a Sentry «no manejado». `handleSendCode` sí tenía
// `finally`, pero sin `catch`: además de salir sin manejar, AVANZABA al paso 2
// y la persona esperaba un código que nunca se envió.
//
// Regla de cada paso: `setPending(step)` → acción → resultado; en excepción se
// clasifica (lib/auth/loginFailure.ts), se reporta solo lo inesperado y se deja
// un `LoginFailure` para que la UI ofrezca «Reintentar»; `setPending(null)` en
// `finally`. Los resultados `{ ok: false }` (credenciales, código vencido,
// límite de intentos) NO son excepciones: van al error del campo, nunca a Sentry.
import type { EmailCodeResult, VerifyCodeResult } from "@/app/actions/emailCodeActions"
import type { LoginResult } from "@/app/actions/postLoginActions"
import {
  classifyLoginFailure,
  shouldReportLoginFailure,
  type LoginFailureKind,
} from "@/lib/auth/loginFailure"

export type LoginStep = "google" | "send_code" | "resend_code" | "verify_code" | "password"

/** Error mostrado bajo un campo; `key` es una clave de `auth.login.*`. */
export type FieldError =
  | { key: "badCode" | "badCredentials" | "invalidEmail" | "invalidPassword" | "employeeHint" }
  | { key: "rateLimited"; seconds: number }

/** Fallo de transporte/servidor: la UI muestra el mensaje y «Reintentar». */
export interface LoginFailure {
  step: LoginStep
  kind: LoginFailureKind
}

export interface LoginHandlerDeps {
  actions: {
    signInWithGoogle(): Promise<{ error: unknown }>
    requestEmailCode(email: string): Promise<EmailCodeResult>
    verifyEmailCode(email: string, code: string): Promise<VerifyCodeResult>
    loginWithPassword(email: string, password: string, next?: string): Promise<LoginResult>
  }
  /** Valores actuales de los campos (se leen al ejecutar, no al crear). */
  read(): { codeEmail: string; code: string; email: string; password: string; next: string }
  setPending(step: LoginStep | null): void
  setFailure(failure: LoginFailure | null): void
  setCodeError(error: FieldError | null): void
  setPasswordError(error: FieldError | null): void
  setOauthError(value: boolean): void
  /** El código salió (o el anti-enumeración finge que salió): pasar al paso 2. */
  onCodeSent(): void
  navigate(path: string): void
  report(err: unknown, ctx: { step: LoginStep; kind: LoginFailureKind }): void
  isOnline(): boolean
  onVersionSkew(err: unknown): void
}

// Sin `retryInSec` (el envío de código no lo devuelve) se dice un minuto: es la
// ventana de los limitadores de auth en lib/ratelimit.ts.
const DEFAULT_RETRY_SEC = 60

function rateLimited(retryInSec?: number): FieldError {
  return { key: "rateLimited", seconds: retryInSec && retryInSec > 0 ? retryInSec : DEFAULT_RETRY_SEC }
}

const hasAt = (email: string) => email.length > 0 && email.includes("@")

export function createLoginHandlers(deps: LoginHandlerDeps): Record<LoginStep, () => Promise<void>> {
  async function guard(step: LoginStep, body: () => Promise<void>): Promise<void> {
    deps.setPending(step)
    deps.setFailure(null)
    try {
      await body()
    } catch (err) {
      const kind = classifyLoginFailure(err, safeOnline())
      deps.setFailure({ step, kind })
      try {
        if (kind === "version_skew") deps.onVersionSkew(err)
        if (shouldReportLoginFailure(kind)) deps.report(err, { step, kind })
      } catch {
        // Reportar nunca puede costarle el login a la persona.
      }
    } finally {
      deps.setPending(null)
    }
  }

  function safeOnline(): boolean | undefined {
    try {
      return deps.isOnline()
    } catch {
      return undefined
    }
  }

  async function sendCode(step: "send_code" | "resend_code"): Promise<void> {
    const email = deps.read().codeEmail.trim()
    if (step === "send_code" && !hasAt(email)) {
      deps.setCodeError({ key: "invalidEmail" })
      return
    }
    deps.setCodeError(null)
    await guard(step, async () => {
      const res = await deps.actions.requestEmailCode(email)
      if (!res.ok && res.reason === "rate_limited") {
        deps.setCodeError(rateLimited())
        return
      }
      // Anti-enumeración: ok, send_failed o invalid_email avanzan igual — un
      // resultado distinto revelaría si el correo tiene cuenta. Una EXCEPCIÓN
      // (arriba, en guard) no revela nada y sí frena: no hubo envío.
      if (step === "send_code") deps.onCodeSent()
    })
  }

  return {
    google: () =>
      guard("google", async () => {
        deps.setOauthError(false)
        const { error } = await deps.actions.signInWithGoogle()
        if (error) deps.setOauthError(true)
      }),

    send_code: () => sendCode("send_code"),

    resend_code: () => sendCode("resend_code"),

    verify_code: () => {
      const { codeEmail, code } = deps.read()
      deps.setCodeError(null)
      return guard("verify_code", async () => {
        const res = await deps.actions.verifyEmailCode(codeEmail.trim(), code.trim())
        if (res.ok) {
          deps.navigate(res.path)
          return
        }
        if (res.reason === "no_role") deps.setCodeError({ key: "employeeHint" })
        else if (res.reason === "rate_limited") deps.setCodeError(rateLimited(res.retryInSec))
        else deps.setCodeError({ key: "badCode" })
      })
    },

    password: () => {
      const { email: rawEmail, password, next } = deps.read()
      const email = rawEmail.trim()
      if (!hasAt(email)) {
        deps.setPasswordError({ key: "invalidEmail" })
        return Promise.resolve()
      }
      if (!password) {
        deps.setPasswordError({ key: "invalidPassword" })
        return Promise.resolve()
      }
      deps.setPasswordError(null)
      return guard("password", async () => {
        // Inicia sesión Y resuelve el destino en una sola llamada de servidor:
        // la cookie de sesión viaja en el Set-Cookie de esta respuesta (ver
        // app/actions/postLoginActions.ts).
        const res = await deps.actions.loginWithPassword(email, password, next || undefined)
        if (res.ok) {
          deps.navigate(res.path)
          return
        }
        if (res.reason === "email_not_confirmed") {
          // Registro a medias: a /verify-email a terminar (reenvía el código).
          deps.navigate(`/verify-email?email=${encodeURIComponent(email)}&resend=1`)
          return
        }
        if (res.reason === "bad_credentials") deps.setPasswordError({ key: "badCredentials" })
        else if (res.reason === "rate_limited") deps.setPasswordError(rateLimited(res.retryInSec))
        else deps.setPasswordError({ key: "employeeHint" })
      })
    },
  }
}
